import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PrinterIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@plerionhq/design-system';
import { ExpandableContainer } from './markdown/ExpandableContainer';
import { useDebouncedValue } from './markdown/useDebouncedValue';
import { ScaledToFit } from './markdown/ScaledToFit';

import { useTranslation } from 'react-i18next';

interface HtmlRendererProps {
  html: string | undefined;
  title?: string;
  isStreaming?: boolean;
}

/**
 * Inject a print page-break helper and a postMessage height-reporter into the HTML.
 * The height reporter is needed because the iframe runs with sandbox="allow-scripts"
 * (no allow-same-origin), so we cannot read contentDocument from the parent.
 *
 * Intentionally minimal — no opinionated base styles are injected.
 * The LLM is responsible for loading all fonts, styles, and libraries it needs.
 */
function prepareHtml(html: string | undefined): string {
  if (!html) {
    return '<html><body></body></html>';
  }

  const printCss = `<style>
  .page-break { page-break-after: always; break-after: page; }
  @media print {
    @page { size: A4; margin: 15mm; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>`;

  // Fires on DOMContentLoaded, load, and twice more for late-rendering libraries.
  const heightScript = `<script>(function(){
  function notify(){
    var h=Math.max(
      document.documentElement.scrollHeight,
      document.body?document.body.scrollHeight:0
    );
    window.parent.postMessage({type:'pleri-html-height',height:h},'*');
  }
  if(document.readyState==='loading'){document.addEventListener('DOMContentLoaded',notify);}
  else{notify();}
  window.addEventListener('load',notify);
  setTimeout(notify,600);
  setTimeout(notify,1800);
})();</script>`;

  let result = html;

  // Inject print CSS before </head>, or prepend if no <head>.
  if (result.includes('</head>')) {
    result = result.replace('</head>', `${printCss}\n</head>`);
  } else {
    result = printCss + '\n' + result;
  }

  // Inject height reporter before </body>, or append if no </body>.
  if (result.includes('</body>')) {
    result = result.replace('</body>', `${heightScript}\n</body>`);
  } else {
    result = result + '\n' + heightScript;
  }

  return result;
}

const MIN_HEIGHT = 400;
const MAX_HEIGHT = 1000;

function DocumentSkeleton() {
  return (
    <div className="animate-pulse space-y-4 p-6">
      {/* Title bar */}
      <div className="h-6 w-48 rounded bg-gray-200 dark:bg-gray-700" />
      {/* Subtitle */}
      <div className="h-3.5 w-72 rounded bg-gray-100 dark:bg-gray-800" />
      <div className="pt-2" />
      {/* Content rows */}
      <div className="space-y-2.5">
        <div className="h-3 w-full rounded bg-gray-100 dark:bg-gray-800" />
        <div className="h-3 w-5/6 rounded bg-gray-100 dark:bg-gray-800" />
        <div className="h-3 w-4/6 rounded bg-gray-100 dark:bg-gray-800" />
      </div>
      {/* Chart placeholder */}
      <div className="mt-4 h-40 w-full rounded-lg bg-gray-100 dark:bg-gray-800" />
      {/* More rows */}
      <div className="space-y-2.5 pt-2">
        <div className="h-3 w-full rounded bg-gray-100 dark:bg-gray-800" />
        <div className="h-3 w-3/4 rounded bg-gray-100 dark:bg-gray-800" />
      </div>
    </div>
  );
}

// Returns true when the HTML has enough body content to be worth showing.
// Intentionally does NOT require </body> — the LLM writes that last, so waiting
// for it would delay the first visible render until the entire document is done.
// Instead we just require a meaningful amount of body text (300 chars) so we
// never commit a blank/stub/head-only render.
// The debounce (1500ms idle / 4000ms max) is what prevents frequent reloads —
// this gate only controls whether a given snapshot is worth committing at all.
function isReadyToRender(html: string | undefined): boolean {
  if (!html) {
    return false;
  }
  const m = html.match(/<body[^>]*>([\s\S]*)/i);
  return !!(m && m[1].trim().length > 300);
}

export const HtmlRenderer: React.FC<HtmlRendererProps> = ({
  html,
  title = 'HTML document',
  isStreaming = false,
}) => {
  const { t } = useTranslation('pleri/chat');
  const [iframeHeight, setIframeHeight] = useState(MIN_HEIGHT);

  // --- Anti-flicker gate ---
  // Only HTML with enough body content enters the debounce pipeline (see isReadyToRender).
  // The ref is updated synchronously during render — safe because refs don't cause re-renders.
  const lastCompleteRef = useRef<string | undefined>(undefined);
  if (isReadyToRender(html)) {
    lastCompleteRef.current = html;
  }

  // Debounce at 1500ms idle / 4000ms max while streaming so the hidden iframe
  // isn't reloading on every chunk. Only complete HTML is debounced (see gate above).
  // When streaming ends, use the raw html immediately for instant display.
  const debouncedHtml = useDebouncedValue(lastCompleteRef.current, 1500, 4000);
  const displayHtml = isStreaming ? debouncedHtml : html;
  const preparedHtml = prepareHtml(displayHtml);

  // --- Double-buffer ---
  // `committed` is the only thing the visible iframe ever shows.
  // A hidden iframe silently loads each new preparedHtml version.
  // We only promote to committed when the hidden load succeeds AND
  // the HTML has real body content — so the visible frame never shows
  // a blank or broken intermediate state.
  // Until the first valid commit, we show the skeleton.
  const [committed, setCommitted] = useState<string | undefined>(undefined);
  const pendingRef = useRef<string>(preparedHtml);
  pendingRef.current = preparedHtml;

  const handlePendingLoad = useCallback(() => {
    const next = pendingRef.current;
    if (isReadyToRender(next)) {
      setCommitted(next);
    }
  }, []);

  const ready = !isStreaming && !!html;

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      // Verify the origin of the message before trusting its contents.
      // Prefer a configured allowed origin; fall back to same-origin.
      const allowedOrigin =
        typeof window !== 'undefined'
          ? process.env.REACT_APP_PLERI_IFRAME_ORIGIN || window.location.origin
          : undefined;

      if (!allowedOrigin || e.origin !== allowedOrigin) {
        return;
      }

      if (
        e.data?.type === 'pleri-html-height' &&
        typeof e.data.height === 'number' &&
        e.data.height > 0
      ) {
        setIframeHeight(
          Math.min(Math.max(e.data.height + 16, MIN_HEIGHT), MAX_HEIGHT),
        );
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const openInNewTab = useCallback((htmlContent: string) => {
    const blob = new Blob([htmlContent], { type: 'text/html; charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);
    const win = window.open(blobUrl, '_blank');
    if (!win) {
      URL.revokeObjectURL(blobUrl);
      return null;
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
    return win;
  }, []);

  const handlePreview = useCallback(() => {
    const htmlContent = committed ?? preparedHtml;
    if (!htmlContent) {
      return;
    }
    openInNewTab(htmlContent);
  }, [committed, preparedHtml, openInNewTab]);

  const handlePrint = useCallback(() => {
    const htmlContent = committed ?? preparedHtml;
    if (!htmlContent) {
      return;
    }
    const win = openInNewTab(htmlContent);
    if (!win) {
      return;
    }
    win.addEventListener('load', () => {
      setTimeout(() => {
        win.focus();
        win.print();
      }, 500);
    });
  }, [committed, preparedHtml, openInNewTab]);

  const containerHeight = ready ? `${iframeHeight + 40}px` : '350px';

  const iconButtonClass =
    'rounded-md border border-gray-200 bg-white p-1.5 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300';

  const toolbar = ready ? (
    <div className="flex items-center gap-1">
      <Tooltip label={t('openPreview')} placement="top">
        <button onClick={handlePreview} className={iconButtonClass}>
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
        </button>
      </Tooltip>
      <Tooltip label={t('printSaveAsPdf')} placement="top">
        <button onClick={handlePrint} className={iconButtonClass}>
          <PrinterIcon className="h-3.5 w-3.5" />
        </button>
      </Tooltip>
    </div>
  ) : undefined;

  return (
    <ExpandableContainer
      title={title}
      minimizedHeight={containerHeight}
      enableScreenshot={false}
      enablePDF={false}
      enableFocus={false}
      toolbar={toolbar}
      printMode="image"
      defaultExpanded={isStreaming}
    >
      {(isExpanded) => (
        <>
          <ScaledToFit designWidth={1400} maxScale={1} enabled={!isExpanded}>
            {/* Visible iframe — only ever shows committed (last valid state).
              Shows skeleton until first valid commit arrives. */}
            {committed ? (
              <iframe
                srcDoc={committed}
                sandbox="allow-scripts"
                style={{
                  width: '100%',
                  height: '100vh',
                  border: 'none',
                }}
                title={title}
              />
            ) : (
              <DocumentSkeleton />
            )}

            {/* Hidden iframe — silently loads the latest preparedHtml.
              Re-keyed on each new version so it always loads fresh.
              Promotes to committed only on successful load with body content. */}
            {displayHtml && (
              <iframe
                key={preparedHtml}
                srcDoc={preparedHtml}
                sandbox="allow-scripts"
                onLoad={handlePendingLoad}
                style={{ display: 'none' }}
                title={t('pendingBadge')}
                aria-hidden="true"
              />
            )}
          </ScaledToFit>
        </>
      )}
    </ExpandableContainer>
  );
};
